import React, { useState, useEffect, useContext } from "react";
import { PricesContext } from "src/shared";
import { Form, InputNumber, Select, DatePicker, AutoComplete, Tabs } from "antd";
import { DebounceSelect } from "src/shared/lib/hooks/context/DebounceSelect";
import { apiRequests } from "src/shared/api/api";
import dayjs from "dayjs";
import debounce from "lodash.debounce";

// eslint-disable-next-line no-template-curly-in-string
const templateRule = "Поле обязательно к заполнению";

export default function Price({ formContext, record, disableNomeclature }) {
  const formItemLayout = { labelCol: { span: 7 }, wrapperCol: { span: 15 } };
  const { priceTypesData, token } = useContext(PricesContext);
  const [priceTypes, setPriceType] = useState([]);
  const [optionsLocation, setOptionsLocations] = useState([]);
  const [activeTab, setActiveTab] = useState("main");

  const fetchNomenclature = async (name) => {
    try {
      const response = await apiRequests.nomenclature.getItems({
        name: name,
        token: token,
      });
      
      if (response.data?.result && Array.isArray(response.data.result)) {
        return response.data.result.map((item) => ({
          label: `${item.name}`,
          value: item.id,
        }));
      }
      return [];
    } catch (error) {
      console.error("Ошибка загрузки номенклатуры:", error);
      return [];
    }
  };

  // Автозаполнение адреса
  const onChangeLocation = async (value) => {
    if (!value) {
      setOptionsLocations([]);
      return;
    }
    try {
      const res = await fetch(
        `https://${process.env.REACT_APP_APP_URL}/api/v1/autosuggestions/geolocation?query=${encodeURIComponent(
          value,
        )}&limit=5`,
      );
      const data = await res.json();
      if (data?.suggestions?.length) {
        setOptionsLocations(
          data.suggestions.map((suggest) => ({
            value: suggest,
            label: suggest,
          })),
        );
      } else {
        setOptionsLocations([
          {
            value: value,
            label: value,
          },
        ]);
      }
    } catch (err) {
      console.error("Ошибка загрузки адресов:", err);
    }
  };

  const debounceOnChangeLocation = debounce(onChangeLocation, 500);

  // Получение координат для выбранного адреса
  const handleAddressSelect = async (address) => {
    formContext.setFieldsValue({ address: address });

    // Получаем координаты для выбранного адреса
    try {
      const res = await fetch(
        `https://${process.env.REACT_APP_APP_URL}/api/v1/autosuggestions/geolocation/validate?address=${encodeURIComponent(
          address,
        )}`,
      );
      const data = await res.json();

      if (data?.latitude !== undefined && data?.longitude !== undefined) {
        formContext.setFieldsValue({
          latitude: data.latitude,
          longitude: data.longitude,
        });
      }
    } catch (err) {
      console.error("Ошибка получения координат:", err);
    }
  };

  // Обработчик изменения адреса при ручном вводе
  const handleAddressChange = (value) => {
    // Устанавливаем значение адреса в форму при каждом изменении
    if (value !== undefined && value !== null) {
      formContext.setFieldsValue({ address: value });
    }
  };

  useEffect(() => {
    if (priceTypesData?.length !== 0) {
      const priceTypeSelect = [];
      for (let item of priceTypesData) {
        const valueObj = { key: item.id, value: item.id, label: item.name };
        if (typeof record !== "undefined" && item.name === record.price_type)
          formContext.setFieldValue("price_type", item.id);
        priceTypeSelect.push(valueObj);
      }
      setPriceType(priceTypeSelect);
    }
  }, [priceTypesData, record, formContext]);

  // Обновляем значения формы при изменении record (как в номенклатуре)
  useEffect(() => {
    if (record) {
      // Преобразуем даты из timestamp в dayjs объекты
      const formValues = {
        ...record,
        date_from: record.date_from ? dayjs.unix(record.date_from) : dayjs(),
        date_to: record.date_to ? dayjs.unix(record.date_to) : dayjs(),
        // Явно устанавливаем адрес и координаты, чтобы они не терялись при обновлении
        address: record.address || undefined,
        latitude: record.latitude || undefined,
        longitude: record.longitude || undefined,
      };
      // Устанавливаем все значения из record, включая address, latitude, longitude
      formContext.setFieldsValue(formValues);
    }
  }, [record, formContext]);

  return (
    <Form
      {...formItemLayout}
      form={formContext}
      initialValues={record ? {
        ...record,
        date_from: record.date_from ? dayjs.unix(record.date_from) : dayjs(),
        date_to: record.date_to ? dayjs.unix(record.date_to) : dayjs(),
        // Явно включаем адрес и координаты в initialValues
        address: record.address || undefined,
        latitude: record.latitude || undefined,
        longitude: record.longitude || undefined,
      } : undefined}
      layout={"horizontal"}
      style={{ maxWidth: "100%" }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "main",
            label: "Основное",
            children: (
              <>
                <Form.Item
                  label={"Номенклатура"}
                  name={"nomenclature"}
                  initialValue={record?.nomenclature_name}
                  rules={[{ required: true, message: templateRule }]}>
                  <DebounceSelect
                    showSearch
                    disabled={disableNomeclature}
                    labelInValue
                    allowClear="true"
                    placeholder="Введите номенклатуру"
                    fetchOptions={fetchNomenclature}
                    onChange={(data) => formContext.setFieldValue("nomenclature", data.key)}
                  />
                </Form.Item>
                <Form.Item
                  initialValue={record?.price}
                  label={"Цена"}
                  name={"price"}
                  rules={[{ required: true, message: templateRule }]}>
                  <InputNumber style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item label={"Тип цены"} name={"price_type"} rules={[{ required: true, message: templateRule }]}>
                  <Select options={priceTypes} />
                </Form.Item>
                <Form.Item
                  label={"Действует от"}
                  name={"date_from"}
                  initialValue={record?.date_from ? dayjs.unix(record?.date_from) : dayjs()}>
                  <DatePicker format={{ format: "DD.MM.YYYY", type: "mask" }} />
                </Form.Item>
                <Form.Item
                  label={"Действует до"}
                  name={"date_to"}
                  initialValue={record?.date_to ? dayjs.unix(record?.date_to) : dayjs()}>
                  <DatePicker format={{ format: "DD.MM.YYYY", type: "mask" }} />
                </Form.Item>
              </>
            ),
          },
          {
            key: "quick",
            label: "Быстро",
            children: (
              <>
                <Form.Item
                  label={"Адрес"}
                  name={"address"}
                  tooltip="При выборе адреса широта и долгота заполняются автоматически"
                >
                  <AutoComplete
                    options={optionsLocation}
                    onSearch={debounceOnChangeLocation}
                    onSelect={handleAddressSelect}
                    onChange={handleAddressChange}
                    filterOption={false}
                    allowClear
                    placeholder="Введите адрес"
                  />
                </Form.Item>
                <Form.Item
                  label={"Широта"}
                  name={"latitude"}
                  normalize={(value) =>
                    value === "" || value === null ? undefined : value
                  }
                  tooltip="Автоматически заполняется при выборе адреса"
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    step={0.000001}
                    placeholder="55.751244"
                    disabled
                  />
                </Form.Item>
                <Form.Item
                  label={"Долгота"}
                  name={"longitude"}
                  normalize={(value) =>
                    value === "" || value === null ? undefined : value
                  }
                  tooltip="Автоматически заполняется при выборе адреса"
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    step={0.000001}
                    placeholder="37.618423"
                    disabled
                  />
                </Form.Item>
              </>
            ),
          },
        ]}
      />
    </Form>
  );
}
